import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Save, Loader2, Palette } from "lucide-react";
import { useSettings, STAGE_COLOR_NAMES, STAGE_COLOR_CLASSES } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";

const inputCls = "px-3 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-sm outline-none focus:border-neutral-900 dark:focus:border-white transition w-full";

function ColorDot({ color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={color}
      className={`h-6 w-6 border ${STAGE_COLOR_CLASSES[color] || ""} ${active ? "ring-2 ring-neutral-900 dark:ring-white ring-offset-1 ring-offset-white dark:ring-offset-neutral-950" : ""}`}
    >&nbsp;</button>
  );
}

function ListEditor({ label, items, setItems, showColor }) {
  const addItem = () => setItems([...items, { id: "", label: "", color: showColor ? "slate" : undefined }]);
  const removeItem = (i) => {
    if (items.length <= 1) { toast.error("At least one item is required"); return; }
    setItems(items.filter((_, idx) => idx !== i));
  };
  const updateItem = (i, patch) => setItems(items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    const next = Array.from(items);
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    setItems(next);
  };

  return (
    <div>
      <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-3">{label}</div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`list-${label}`}>
          {(provided) => (
            <ul ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
              {items.map((it, i) => (
                <Draggable draggableId={`row-${label}-${i}`} index={i} key={`row-${label}-${i}`}>
                  {(dp) => (
                    <li ref={dp.innerRef} {...dp.draggableProps} className="flex items-start gap-2 p-2 border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950" data-testid={`settings-item-${label}-${i}`}>
                      <span {...dp.dragHandleProps} className="mt-2 text-neutral-400 cursor-grab active:cursor-grabbing"><GripVertical size={14}/></span>
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input data-testid={`settings-label-${label}-${i}`} className={inputCls} placeholder="Label" value={it.label || ""} onChange={(e) => updateItem(i, { label: e.target.value })} />
                        {showColor && (
                          <div className="flex items-center gap-1 flex-wrap">
                            <Palette size={14} className="text-neutral-400 mr-1"/>
                            {STAGE_COLOR_NAMES.map((c) => (
                              <ColorDot key={c} color={c} active={c === it.color} onClick={() => updateItem(i, { color: c })} />
                            ))}
                          </div>
                        )}
                      </div>
                      <button onClick={() => removeItem(i)} data-testid={`settings-remove-${label}-${i}`} className="text-neutral-400 hover:text-rose-600 mt-2"><Trash2 size={14}/></button>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
      <button onClick={addItem} data-testid={`settings-add-${label}`} className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-neutral-200 dark:border-neutral-800 text-[11px] tracking-overline uppercase hover:bg-neutral-100 dark:hover:bg-neutral-900"><Plus size={12}/> Add {label.slice(0, -1)}</button>
    </div>
  );
}

function SettingsSection({ title, description, storeKey, showColor }) {
  const { settings, update } = useSettings();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setItems(settings[storeKey] || []); }, [settings, storeKey]);

  const save = async () => {
    setSaving(true);
    try {
      await update(storeKey, items);
      toast.success(`${title} saved`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <section className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950" data-testid={`settings-section-${storeKey}`}>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h2 className="font-serif text-2xl">{title}</h2>
          {description && <p className="text-sm text-neutral-500 mt-1 max-w-lg">{description}</p>}
        </div>
        <button onClick={save} disabled={saving} data-testid={`settings-save-${storeKey}`} className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
        </button>
      </div>
      <ListEditor label={storeKey} items={items} setItems={setItems} showColor={showColor} />
    </section>
  );
}

export default function CrmSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <div className="text-[11px] tracking-overline uppercase text-neutral-500">Customise</div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl">Settings</h1>
        <p className="mt-3 text-sm text-neutral-500 max-w-2xl">
          Rename, reorder, add or remove pipeline stages, services and lead sources.
          Changes apply immediately across the CRM. Renaming a stage keeps every existing lead on that stage — nothing is lost.
        </p>
      </div>

      {!isAdmin && (
        <div className="mb-6 p-4 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 text-sm">
          Only admins can edit these settings. Contact Chloe if you need a change.
        </div>
      )}

      <div className={`space-y-8 ${!isAdmin ? "opacity-60 pointer-events-none" : ""}`}>
        <SettingsSection
          storeKey="stages"
          title="Pipeline stages"
          description="These are the columns on your Kanban board and the statuses a lead can be in. Drag to reorder, pick a colour per stage. Removing a stage moves any leads on it to the first stage."
          showColor
        />
        <SettingsSection
          storeKey="services"
          title="Services"
          description="The list of services shown on lead forms and used to categorise leads."
        />
        <SettingsSection
          storeKey="sources"
          title="Lead sources"
          description="Where your leads come from — used to track marketing performance."
        />
      </div>
    </div>
  );
}
